import { isBefore } from 'date-fns';
import { addHours } from 'date-fns/addHours';
import { OrderState } from '@eti/schema-types';
import type { OrderListType, OrderType } from '../models/models';
import {
  createBookingHotelsDeeplink,
  createBookingCarsDeeplink,
} from '../../../common/deeplinking/booking';

export const getOrderRelatedDates = (
  order: OrderType,
): { lastArrivalDate: Date; currentDate: Date } => {
  const lastArrivalDate = new Date(order.bounds[order.bounds.length - 1].arrivedAt);
  const currentDate = addHours(new Date(), 2);
  return { lastArrivalDate, currentDate };
};

export const isArchived = (order: OrderType): boolean => {
  const { lastArrivalDate, currentDate } = getOrderRelatedDates(order);
  return isBefore(lastArrivalDate, currentDate);
};

export const isCancelled = (order: OrderType): boolean => order.orderState === OrderState.Canceled;

export const isChanged = (order: OrderType): boolean => order.orderState === OrderState.Changed;

const getOrderForDeeplinkUrl = (selectedOrder: OrderType | null, upcomingOrders: OrderListType) => {
  if (selectedOrder) {
    if (!isArchived(selectedOrder) && !isCancelled(selectedOrder) && !isChanged(selectedOrder)) {
      return selectedOrder;
    }
    return null;
  }

  if (upcomingOrders.length === 1) {
    const singleUpcomingOrder = upcomingOrders[0];
    if (!isCancelled(singleUpcomingOrder) && !isChanged(singleUpcomingOrder)) {
      return singleUpcomingOrder;
    }
  }

  return null;
};

export const getDeeplinkForHotelBanner = (
  affiliateId: string,
  selectedOrder: OrderType | null,
  upcomingOrders: OrderListType,
  languageCode: string,
  siteCurrencyCode: string,
) => {
  const orderForHotelBanner = getOrderForDeeplinkUrl(selectedOrder, upcomingOrders);
  return orderForHotelBanner
    ? createBookingHotelsDeeplink(
        orderForHotelBanner.bounds[0].arrivedAt,
        orderForHotelBanner.bounds[1]?.departuredAt,
        orderForHotelBanner.bounds[0].destinationCityCode,
        0,
        0,
        languageCode,
        siteCurrencyCode,
        `pagemanagebooking-link-${affiliateId}-click`,
        affiliateId,
      )
    : null;
};

export const getDeeplinkForCarBanner = (
  affiliateId: string,
  selectedOrder: OrderType | null,
  upcomingOrders: OrderListType,
) => {
  const orderForHotelBanner = getOrderForDeeplinkUrl(selectedOrder, upcomingOrders);
  return orderForHotelBanner
    ? createBookingCarsDeeplink(
        orderForHotelBanner.bounds[0].arrivedAt,
        orderForHotelBanner.bounds[1]?.departuredAt,
        orderForHotelBanner.bounds[0].destination,
        orderForHotelBanner.bounds[0].destinationCityCode,
        affiliateId,
        `pagemanagebooking-CARlink-${affiliateId}-click`,
      )
    : null;
};

export const isWhiteLabel = (brandCode: string, whiteLabels?: string[]): boolean => {
  return Boolean(
    whiteLabels?.map((whiteLabel) => whiteLabel.trim().toLowerCase()).includes(brandCode),
  );
};
